/**
 * Status transitions that require a comment (SD-145).
 *
 * Admins can mark individual statuses `requiresComment`. A person moving a
 * task INTO such a status must supply a `statusComment`; the server posts it
 * to the task's comment thread in the same transaction as the status write
 * and rejects the change with a 400 carrying `STATUS_COMMENT_REQUIRED_CODE`
 * when it is missing. System actors (automations, imports, sync) are exempt.
 */

/** Longest status comment accepted (plain text, before HTML escaping). */
export const STATUS_COMMENT_MAX_LENGTH = 5000

/** Machine-readable error code on the 400 returned when a comment is missing. */
export const STATUS_COMMENT_REQUIRED_CODE = 'STATUS_COMMENT_REQUIRED'

/** Body of the 400 returned when a status change is missing its comment. */
export interface StatusCommentRequiredErrorBody {
  code: typeof STATUS_COMMENT_REQUIRED_CODE
  message: string
  statusId: string
  statusName: string
}

/** User-facing copy for a missing status comment. */
export const statusCommentRequiredMessage = (statusName: string): string =>
  `Moving to ${statusName.trim() || 'this status'} requires a comment`

/** Trimmed comment text, or null when nothing meaningful was supplied. */
export const normalizeStatusComment = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** Whether a status definition asks for a comment on entry. */
export const statusRequiresComment = (
  status: { requiresComment?: boolean | null } | null | undefined,
): boolean => status?.requiresComment === true

/**
 * Extract the structured body from a raw 400 body or from an error carrying
 * it in `data` (the web ApiError shape). Null for any other error.
 */
export const readStatusCommentRequiredError = (
  value: unknown,
): StatusCommentRequiredErrorBody | null => {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const candidate =
    record.code === STATUS_COMMENT_REQUIRED_CODE
      ? record
      : record.data && typeof record.data === 'object'
        ? (record.data as Record<string, unknown>)
        : null
  if (!candidate || candidate.code !== STATUS_COMMENT_REQUIRED_CODE) return null
  const statusName =
    typeof candidate.statusName === 'string' ? candidate.statusName : ''
  return {
    code: STATUS_COMMENT_REQUIRED_CODE,
    message:
      typeof candidate.message === 'string'
        ? candidate.message
        : statusCommentRequiredMessage(statusName),
    statusId: typeof candidate.statusId === 'string' ? candidate.statusId : '',
    statusName,
  }
}
